import { css } from '@emotion/react';
import React, { useEffect, useRef, useState } from 'react';
import { AiProfileInfo, OnboardingAiProfileType } from '@/customTypes/chapter';

import { AiCharacterImageType, MainCharacter } from '@/customTypes/storygame';
import StoryGameOnboardingAlert, {
  IStoryGameOnboardingAlert,
} from '@/components/storyGame/popup/StoryGameOnboardingAlert';
import Image from 'next/image';
import { StoryGameArrowLeft, StoryGameArrowRight } from 'public/assets';
import MobileStoryGamePreviewCharacterName from './MobileStoryGamePreviewCharacterName';
import { isMobileDevice } from '@/common/utils';
import useTranslation from 'next-translate/useTranslation';

interface StoryGameAiImagePreviewProps {
  setAiProfileType: (type: OnboardingAiProfileType) => void;
  onNext: () => void;
  onChangeAiProfile: (newAiProfileInfo: AiProfileInfo) => void;
  onGetAiCharacterSessionId: () => Promise<void>;

  onGetAiCharacterImageList: (chrId?: number) => Promise<void>;
  aiCharacterImageList?: AiCharacterImageType[]; //profileImage는 default캐릭터에만 있음
  onClickStoryGameCharacter: (character: AiProfileInfo) => void;
  selectedCharacter?: MainCharacter;

  onCreateCharacter: () => Promise<void>;
  aiProfileInfo?: AiProfileInfo;
  aiProfileInfoArray?: AiProfileInfo[];
  addOpenedStorygameIds: () => void;
  openedStorygameIds?: string;
  isShowPromotionStorygame: boolean;
  setIsShowPromotionStorygame: (value: boolean) => void;
  bg?: string;
  setIsStoryGameEdit: (value: boolean) => void;
  selectPreviousCharacter: () => void;
  selectNextCharacter: () => void;
  storygameAlertData: IStoryGameOnboardingAlert;
  setStorygameAlertData: (data: IStoryGameOnboardingAlert) => void;
  setStorygameToastData: (data: {
    message: string;
    isVisible: boolean;
  }) => void;
  removeSessionId: () => void;
}

const StoryGameAiImagePreview = ({
  onNext,
  onChangeAiProfile,
  aiCharacterImageList,
  onClickStoryGameCharacter,
  selectedCharacter,
  aiProfileInfo,
  aiProfileInfoArray,
  setIsStoryGameEdit,
  bg,
  selectPreviousCharacter,
  selectNextCharacter,
  storygameAlertData,
  setStorygameAlertData,
  setStorygameToastData,
  removeSessionId,
}: StoryGameAiImagePreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();

  const [isSwiping, setIsSwiping] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const slideRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    if (aiProfileInfoArray) {
      if (currentIndex < aiProfileInfoArray.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setStorygameToastData({
          message: t('storygame_ai_onboarding_screen_description_last_page'),
          isVisible: true,
        });
      }
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsSwiping(true);
    setStartX(e.touches[0].clientX);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsSwiping(true);
    setStartX(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) return;
    const currentX = e.touches[0].clientX;
    const diff = startX - currentX;
    setCurrentX(diff);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isSwiping) return;
    const currentX = e.clientX;
    const diff = startX - currentX;
    setCurrentX(diff);
  };

  const handleSwiping = () => {
    if (!isSwiping) return;
    setIsSwiping(false);
    const threshold = 100; // minimum distance to be considered as swipe
    if (currentX > threshold) {
      nextSlide();
    } else if (currentX < -threshold) {
      prevSlide();
    }
    setCurrentX(0);
  };

  useEffect(() => {
    const handleTouchEnd = () => handleSwiping();
    const handleMouseUp = () => handleSwiping();

    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isSwiping, currentX]);
  return (
    <div css={styles(isMobileDevice())}>
      <div className="bg" />
      <div className="top-container">
        {/* <div className="top-left">
          <HomeIcon />
        </div> */}
        <div className="top-title">
          {t('storygame_ai_onboarding_screen_title_my_character')}
        </div>
        <div className="top-right" onClick={() => setIsStoryGameEdit(true)}>
          {t('comment_popup_button_edit')}
        </div>
      </div>

      <div className="container">
        <div className="carousel-wrapper">
          <button className="nav-button left" onClick={prevSlide}>
            <StoryGameArrowLeft
              color={currentIndex === 0 ? '#FFFFFF40' : 'white'}
            />
          </button>
          <>
            <div className="carousel-container">
              <div
                ref={slideRef}
                className="carousel-slide"
                style={{
                  transform: `translateX(calc(-${
                    currentIndex * 100
                  }% - ${currentX}px))`,
                  transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
              >
                {aiProfileInfoArray?.map((profile) => (
                  <div
                    key={profile.characterId}
                    className="image-container"
                    onClick={() =>
                      !isSwiping && onClickStoryGameCharacter(profile)
                    }
                  >
                    <Image
                      src={profile?.portraitFile?.link ?? ''}
                      className="ai_character"
                      objectFit="contain"
                      alt="AI character"
                      layout="fill"
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
          <button className="nav-button right" onClick={nextSlide}>
            <StoryGameArrowRight
              color={
                currentIndex === (aiProfileInfoArray?.length ?? 0) - 1
                  ? '#FFFFFF40'
                  : 'white'
              }
            />
          </button>
        </div>

        <div className="side-container-bottom">
          {selectedCharacter && aiProfileInfoArray?.[currentIndex] && (
            <MobileStoryGamePreviewCharacterName
              characterImage={
                aiProfileInfoArray?.[currentIndex]?.profileFile ??
                aiProfileInfoArray?.[currentIndex]?.defaultProfileImageFile
                  ?.link
              }
              firstName={aiProfileInfoArray?.[currentIndex]?.firstName ?? ''}
              lastName={aiProfileInfoArray?.[currentIndex]?.lastName ?? ''}
              displayName={
                aiProfileInfoArray?.[currentIndex]?.displayName ?? ''
              }
              onChangeAiProfile={onChangeAiProfile}
              onProfileEdit={function (): void {
                setIsStoryGameEdit(true);
              }}
              isDefault={
                !!aiCharacterImageList?.find((characterImage) => {
                  return (
                    characterImage.profileImage?.fileId ===
                    aiProfileInfoArray?.[currentIndex]?.defaultProfileImageFile
                      ?.fileId
                  );
                })?.isDefault
              }
              onNext={nextSlide}
              onPrevious={prevSlide}
              dotCount={aiProfileInfoArray?.length ?? 0}
              currentDotIndex={currentIndex}
            />
          )}
          <div
            className="start-button"
            onClick={() => {
              setStorygameAlertData({
                title: t(
                  'storygame_ai_onboarding_popup_description_alert_complete_and_start',
                ),
                subtitle: t(
                  'storygame_ai_onboarding_popup_description_alert_info_cannot_change',
                ),
                onCancel: () => {
                  setStorygameAlertData({
                    ...storygameAlertData,
                    isVisible: false,
                  });
                },
                confirmText: t('common_screen_button_start'),
                onConfirm: () => {
                  setStorygameAlertData({
                    ...storygameAlertData,
                    isVisible: false,
                  });
                  removeSessionId();
                  onNext();
                },
                isVisible: true,
              });
            }}
          >
            {t('storygame_ai_onboarding_screen_button_start_game')}
          </div>
        </div>
      </div>
      <StoryGameOnboardingAlert storygameAlertData={storygameAlertData} />
    </div>
  );
};

const styles = (isMobileDevice: boolean) => css`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  // top: env(safe-area-inset-top);

  .bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #0000001a;
    backdrop-filter: blur(8px);
  }
  .top-container {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;

    padding: 12px;
    height: ${isMobileDevice ? '48px' : '56px'};
    justify-content: center;

    z-index: 1;
    // background: linear-gradient(
    //   180deg,
    //   rgba(0, 0, 0, 0.25) 0%,
    //   rgba(0, 0, 0, 0) 100%
    // );
  }

  .start-button {
    color: #fff;
    font-size: 18px;
    font-weight: 700;

    display: flex;
    padding: 18px 0px;
    min-height: 48px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    background: #753bbd;
    cursor: pointer;
    width: 100%;
  }

  .top-container .top-title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 500;
    position: absolute;
  }
  .top-container .top-right {
    position: absolute;
    right: 12px;
    display: flex;
    height: 40px;
    min-height: 40px;
    padding: 12px 16px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    font-size: 14px;
    color: #fff;
    cursor: pointer;
    border: 1px solid rgba(255, 255, 255, 0.25);
    background: rgba(0, 0, 0, 0.25);
  }

  .container {
    padding-top: 1.5rem;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }

  .image-container {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center; /* 세로 중앙 정렬 */
    overflow: hidden;
    position: relative;
    height: 80vh;
    max-height: 100%;
  }

  .image-container .ai_character {
    width: auto !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    object-fit: contain !important; /* cover 대신 contain 사용 */
    object-position: center center !important; /* 중앙 정렬 */
    position: relative !important;
    left: 50% !important; /* 가로 중앙 정렬을 위해 추가 */
    transform: translateX(-50%) !important; /* 가로 중앙 정렬을 위해 추가 */
  }

  .title {
    text-align: center;
    margin-bottom: 20px;
    color: #fff;

    font-size: 18px;
    font-weight: 500;
  }

  .loading-text1 {
    margin-top: 5px;
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    text-align: center;
    color: #ffffff;
  }
  .loading-text2 {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: center;
    color: #ffffffbf;
  }

  .nav-button {
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    padding: 10px;
    z-index: 1;
    &:hover {
      opacity: 0.8;
    }
  }

  .nav-button.left {
    position: absolute;
    left: 0.5rem;
  }

  .nav-button.right {
    position: absolute;
    right: 0.5rem;
  }

  // @media screen and (max-width: 1024px) {
  //   width: 1024px;
  // }

  .carousel-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
  }

  .carousel-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    touch-action: pan-y;
  }

  .carousel-slide {
    display: flex;
    transition: transform 0.3s ease-out;
    height: 100%;
    align-items: end;
  }

  .image-container {
    flex: 0 0 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: relative;
  }

  .ai_character {
    width: auto !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    object-fit: contain !important;
    object-position: center center !important;
    position: relative !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
  }

  .side-container-bottom {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 0px 16px;
    scrollbar-width: none;
    -ms-overflow-style: none;
    height: 11.3rem;
    margin: 0px 0px calc(env(safe-area-inset-top) + 16px) 0px;
    position: absolute;
    bottom: 0px;
  }
`;

// const loadingCss = (size: string) => css`
//   justify-content: center;
//   align-items: center;
//   display: flex;
//   flex-direction: column;
//   .loading {
//     width: ${size ? size : '8em'};
//     height: ${size ? size : '8em'};
//     border-radius: 50%;
//     border: 5px solid ${theme.colors.primary500};
//     border-top: 3px solid transparent;
//     animation: spin 1s linear infinite;
//     @media screen and (max-width: ${theme.devices.mobile}) {
//       width: ${size ? size : '4em'};
//       height: ${size ? size : '4em'};
//       border-width: 3px;
//     }
//   }
// `;

export default StoryGameAiImagePreview;
